import { useMemo } from 'react';

export interface UseContextualThinkingPhrasesResult {
  /**
   * Context-aware phrases to feed into `ChatLoadingIndicator`, or `undefined`
   * to fall back to the generic rotating set. Stays `undefined` while a request
   * is in flight so the loader shows the generic phrases in the meantime.
   */
  phrases: string[] | undefined;
  /** Whether contextual phrases are currently being generated. */
  isLoading: boolean;
}

const EMPTY_RESULT: UseContextualThinkingPhrasesResult = {
  phrases: undefined,
  isLoading: false,
};

/**
 * Sources context-aware "thinking" phrases for the loading indicator.
 *
 * The intent: instead of showing generic narration ("Contemplating the query…"),
 * derive a few short, query-specific phrases from a fast/cheap model (Haiku via
 * the ml-scribe backend) so the loader reads as if it understands what the user
 * asked.
 *
 * This hook is the frontend seam for that pipeline. It is deliberately a no-op
 * today — it returns `undefined`, so `ChatLoadingIndicator` keeps its current
 * generic behaviour — and is the single place to wire the backend call once the
 * endpoint exists. Keeping the seam here means surfaces (chat, agents, evidence)
 * opt in just by reading this hook and passing `phrases` down; no presentational
 * changes are needed when the backend lands.
 *
 * @param query - The user's query for the current turn (empty/undefined disables).
 */
export function useContextualThinkingPhrases(
  query: string | undefined
): UseContextualThinkingPhrasesResult {
  return useMemo(() => {
    if (!query?.trim()) return EMPTY_RESULT;

    // TODO(haiku-loader): call the ml-scribe Haiku phrase endpoint here, cache
    // per-query, and return the generated phrases. Until then we intentionally
    // fall back to the generic set so behaviour is unchanged.
    return EMPTY_RESULT;
  }, [query]);
}
